// PARITY-0 (undo records, client half; hybrid design §1 principle 3 "Undo over confirm"): a
// reversible write answers an `undo_id` (services/api domain/undo); `withUndo` turns it into the
// toast's Undo button. Undo calls `POST undo/{id}`, which the server refuses with the reason
// when the subject changed since ("Sam changed the status after this"), when 15 minutes passed,
// or when the caller did not make the write; the refusal is shown as it reads. A write that
// changed nothing answers `undo_id: null` and gets a plain toast. Non-reversible effects never
// carry an `undo_id`, so they never show Undo.
import { toast } from "@companyos/ui";
import type { Api } from "../../api/client";
import { problemText } from "../settings/problem-text";

export type UndoResult = Awaited<ReturnType<Api["client"]["undo"]["apply"]>>;

export type WithUndo = {
  title: string;
  description?: string;
  /** The write's `undo_id` (null or absent: nothing to undo). */
  undoId: string | null | undefined;
  /** After the server put it back (refresh caches with `result.task`). */
  onUndone?: (result: UndoResult) => void;
};

/**
 * Reverses a write (`POST undo/{id}`); rejects with the server's refusal. DECISION-LEDGER: an
 * optional reason, kept on the undo and on the agent's record when the write was an agent's.
 */
export function applyUndo(
  api: Api,
  co: string,
  undoId: string,
  reason?: string,
): Promise<UndoResult> {
  const why = reason?.trim();
  return api.client.undo.apply({ params: { co, id: undoId }, body: why ? { reason: why } : {} });
}

/** Shows the write's toast, with Undo when the write left an undo record. */
export function withUndo(api: Api, co: string, o: WithUndo): void {
  const base = { title: o.title, ...(o.description ? { description: o.description } : {}) };
  const undoId = o.undoId;
  if (!undoId) {
    toast.add({ ...base, type: "success" });
    return;
  }
  toast.add({
    ...base,
    type: "success",
    actionProps: {
      children: "Undo",
      onClick: () => {
        applyUndo(api, co, undoId).then(
          (result) => {
            o.onUndone?.(result);
            toast.add({ title: "Undone", type: "success" });
          },
          (e: unknown) =>
            toast.add({
              title: "Not undone",
              description: problemText(e, "This change could not be undone"),
              type: "error",
            }),
        );
      },
    },
  });
}
